import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { orpc } from "@/lib/orpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldInfo, TimezoneField } from "@/routes/stores/-components/fields";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/gu, "-")
    .replaceAll(/^-+|-+$/gu, "")
    .slice(0, 40);
}

export function RegisterStoreDialog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [timezone, setTimezone] = useState("UTC");
  const [bucket, setBucket] = useState("retail-sessions");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation(
    orpc.store.create.mutationOptions({
      onSuccess: async (store) => {
        await queryClient.invalidateQueries({ queryKey: orpc.store.key() });
        toast.success(`Store "${store.slug}" registered`);
        setOpen(false);
        void navigate({ to: "/stores/$storeSlug", params: { storeSlug: store.slug } });
      },
      onError: (err) => setError(err.message),
    }),
  );

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    create.mutate({
      slug,
      name,
      timezone,
      objectStore: { bucket },
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Register store</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Register a store</DialogTitle>
          <DialogDescription>
            The store ID names this store's folder in storage and labels its data everywhere else.
            It cannot be changed later.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <div className="space-y-1">
            <Label htmlFor="store-name">Name</Label>
            <Input
              id="store-name"
              value={name}
              required
              placeholder="Downtown flagship"
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(slugify(e.target.value));
              }}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="store-slug">Store ID</Label>
              <FieldInfo label="Store ID">
                Lowercase letters, digits, hyphens. Used as the bucket folder name.
              </FieldInfo>
            </div>
            <Input
              id="store-slug"
              value={slug}
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              placeholder="store-001"
              data-numeric
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
            />
          </div>
          <TimezoneField
            id="store-timezone"
            label="Timezone"
            value={timezone}
            onChange={setTimezone}
            required
            placeholder="Asia/Kolkata"
          />
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="store-bucket">Storage bucket</Label>
              <FieldInfo label="Storage bucket">
                Each night's recordings are expected at {bucket || "your-bucket"}/
                {slug || "store-id"}/&lt;date&gt;/
              </FieldInfo>
            </div>
            <Input
              id="store-bucket"
              value={bucket}
              required
              data-numeric
              onChange={(e) => setBucket(e.target.value)}
            />
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending || !slug || !name || !bucket}>
              {create.isPending ? "Registering" : "Register"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
